// ABOUTME: utility for managing CSS custom properties based on theme colors and dark mode state
// ABOUTME: handles dynamic application of tenant branding colors and semantic color variables

interface ColorPalette {
  50: string;
  100: string;
  200: string;
  300: string;
  400: string;
  500: string;
  600: string;
  700: string;
  800: string;
  900: string;
}

export const setCSSVariables = (
  colorPalette: ColorPalette,
  primaryColor: string,
  isDark: boolean,
) => {
  if (typeof document === "undefined") return;

  const root = document.documentElement;

  // set theme attribute for CSS targeting
  root.setAttribute("data-theme", isDark ? "dark" : "light");

  // primary color variations - use palette main color for consistency
  const mainColor = isDark ? colorPalette[500] : primaryColor;
  root.style.setProperty("--color-primary", mainColor);
  root.style.setProperty("--color-primary-light", colorPalette[300]);
  root.style.setProperty("--color-primary-dark", colorPalette[700]);

  // theme shades for backgrounds and borders
  root.style.setProperty("--primary-50", colorPalette[50]);
  root.style.setProperty("--primary-100", colorPalette[100]);
  root.style.setProperty("--primary-200", colorPalette[200]);
  root.style.setProperty("--primary-300", colorPalette[300]);
  root.style.setProperty("--primary-400", colorPalette[400]);
  root.style.setProperty("--primary-500", colorPalette[500]);
  root.style.setProperty("--primary-600", colorPalette[600]);
  root.style.setProperty("--primary-700", colorPalette[700]);
  root.style.setProperty("--primary-800", colorPalette[800]);
  root.style.setProperty("--primary-900", colorPalette[900]);

  // hover and interaction states
  root.style.setProperty("--primary-hover-bg", `${mainColor}25`); // 15% opacity
  root.style.setProperty("--primary-hover-border", `${colorPalette[700]}40`); // 25% opacity
  root.style.setProperty("--primary-subtle-bg", `${colorPalette[300]}15`); // 8% opacity
  root.style.setProperty("--primary-focus-ring", `${colorPalette[500]}1a`); // 10% opacity for focus rings
  root.style.setProperty("--primary-shadow", `${colorPalette[600]}4d`); // 30% opacity for shadows
  root.style.setProperty(
    "--primary-border-subtle",
    isDark ? `${colorPalette[800]}40` : `${colorPalette[200]}80`,
  ); // very subtle themed borders

  // semantic background and text colors - true neutrals
  if (isDark) {
    // dark mode semantic colors - warm neutral grays
    root.style.setProperty("--background-primary", "#0a0a0a");
    root.style.setProperty("--background-secondary", "#171717");
    root.style.setProperty("--background-elevated", "#262626");
    root.style.setProperty("--text-primary", "#fafafa");
    root.style.setProperty("--text-secondary", "#d4d4d4");
    root.style.setProperty("--text-tertiary", "#a3a3a3");
    root.style.setProperty("--border-primary", "#404040");
    root.style.setProperty("--border-secondary", "#262626");
  } else {
    // light mode semantic colors
    root.style.setProperty("--background-primary", "#ffffff");
    root.style.setProperty("--background-secondary", "#fafafa");
    root.style.setProperty("--background-elevated", "#ffffff");
    root.style.setProperty("--text-primary", "#171717");
    root.style.setProperty("--text-secondary", "#525252");
    root.style.setProperty("--text-tertiary", "#737373");
    root.style.setProperty("--border-primary", "#e5e5e5");
    root.style.setProperty("--border-secondary", "#f5f5f5");
  }

  // gray scale palette - true neutral grays
  if (isDark) {
    root.style.setProperty("--gray-50", "#0a0a0a");
    root.style.setProperty("--gray-100", "#171717");
    root.style.setProperty("--gray-200", "#262626");
    root.style.setProperty("--gray-300", "#404040");
    root.style.setProperty("--gray-400", "#525252");
    root.style.setProperty("--gray-500", "#737373");
    root.style.setProperty("--gray-600", "#a3a3a3");
    root.style.setProperty("--gray-700", "#d4d4d4");
    root.style.setProperty("--gray-800", "#e5e5e5");
    root.style.setProperty("--gray-900", "#fafafa");
  } else {
    root.style.setProperty("--gray-50", "#fafafa");
    root.style.setProperty("--gray-100", "#f5f5f5");
    root.style.setProperty("--gray-200", "#e5e5e5");
    root.style.setProperty("--gray-300", "#d4d4d4");
    root.style.setProperty("--gray-400", "#a3a3a3");
    root.style.setProperty("--gray-500", "#737373");
    root.style.setProperty("--gray-600", "#525252");
    root.style.setProperty("--gray-700", "#404040");
    root.style.setProperty("--gray-800", "#262626");
    root.style.setProperty("--gray-900", "#171717");
  }

  // update link colors to match tenant theme
  root.style.setProperty("--link-color", mainColor);
  root.style.setProperty("--link-color-hover", colorPalette[700]);
  root.style.setProperty("--link-color-active", colorPalette[800]);
};
